/**
 * Spécialités V2 — pack +33 spé pour combler les manques vs Doctolib/Dabadoc.
 *
 * Audit 2026-05-11 : Sahha avait 63 spé en DB (60 en static) vs ~94 chez
 * Doctolib. Ce pack ajoute :
 *   • 3 reconciliations (slug en static mais absent DB : infirmier, opticien,
 *     orthophoniste)
 *   • 7 paramédicaux clés (audioprothésiste, orthoptiste, ergothérapeute,
 *     psychomotricien, diététicien, manipulateur radio, infirmier domicile)
 *   • 7 sous-spé médicales (cardiopédiatre, gynéco-obstétricien, médecine
 *     reproduction/douleur, soins palliatifs, échographiste, médecin nucléaire)
 *   • 5 médecines alternatives (acupuncteur, homéopathe, chiropracteur,
 *     naturopathe, tabacologue)
 *   • 5 chirurgies fines (main, oral, endocrinien, oncochirurgien, face/cou)
 *   • 6 médecine académique/hospitalière (addictologue, infectiologue,
 *     génétique, cancérologue, légiste, pharmacien d'officine)
 *
 * Format aligné avec SPECIALTIES dans specialties.ts (descriptions trilingues,
 * commonConditions pour BookingFlow + search, averagePriceMad pour SEO).
 *
 * Importé en cascade dans :
 *   • specialties.ts → concat dans SPECIALTIES exporté
 *   • prisma/seed.ts → upsert idempotent en DB
 */

import type { SpecialtySeed } from "./specialties";

export const SPECIALTIES_V2: SpecialtySeed[] = [
  // NOTE 2026-06-01 : Les 3 reconciliations (infirmier / opticien / orthophoniste)
  // ont été fusionnées dans specialties.ts (canonique). Voir commit dédup. Le
  // .find() retournait déjà la V1 en premier → V2 ici était dead code.

  // ═══════════════════ MÉDECINE D'URGENCE ═══════════════════
  // Ajouté 2026-06-04 — découverte Bug 4 (24 médecins sans isPrimary) : les
  // imports docexpress assignaient "cardiologue" comme fallback aux médecins
  // dont le slug indiquait "urgentiste" car la spe n'existait pas au seed.
  // Slugs DB affectés : dr-X-urgentiste-tanger (Sara El Mehdi, Nadia Bennani,
  // Fatima Zahra Cherkaoui, Zineb Lahlou, Karim El Ouardi).
  {
    slug: "urgentiste",
    nameFr: "Médecin urgentiste",
    nameAr: "طبيب الطوارئ",
    nameEn: "Emergency physician",
    icon: "🚨",
    featured: false,
    descriptionFr:
      "Le médecin urgentiste prend en charge les situations médicales aiguës nécessitant une intervention rapide : malaises, traumatismes, douleurs thoraciques, détresse respiratoire, urgences pédiatriques.",
    descriptionAr:
      "يتولى طبيب الطوارئ الحالات الطبية الحادة التي تتطلب تدخلاً سريعاً: الإغماء، الإصابات، آلام الصدر، ضيق التنفس، طوارئ الأطفال.",
    descriptionEn:
      "The emergency physician handles acute medical situations requiring rapid intervention: faintness, trauma, chest pain, respiratory distress, paediatric emergencies.",
    commonConditions: ["Traumatisme", "Douleur thoracique", "Détresse respiratoire", "Malaise", "Urgence pédiatrique"],
    commonConditionsAr: ["الإصابات", "آلام الصدر", "ضيق التنفس", "الإغماء", "طوارئ الأطفال"],
    commonConditionsEn: ["Trauma", "Chest pain", "Respiratory distress", "Faintness", "Paediatric emergency"],
    averagePriceMad: { min: 300, max: 800 },
  },

  // ═══════════════════ PARAMÉDICAUX ═══════════════════
  {
    slug: "infirmier-domicile",
    nameFr: "Infirmier à domicile",
    nameAr: "ممرض في المنزل",
    nameEn: "Home care nurse",
    icon: "🏠",
    featured: false,
    descriptionFr:
      "L'infirmier(ère) à domicile assure les soins quotidiens chez vous : pansements, injections, perfusions, toilette médicalisée. Idéal après hospitalisation ou pour pathologies chroniques.",
    descriptionAr:
      "يضمن الممرض(ة) في المنزل الرعاية اليومية لديك: الضمادات، الحقن، التسريب، النظافة الطبية. مثالي بعد الاستشفاء أو للأمراض المزمنة.",
    descriptionEn:
      "The home care nurse provides daily care at your home: dressings, injections, IVs, medical hygiene. Ideal after hospitalization or for chronic conditions.",
    commonConditions: ["Soins post-opératoires", "Pansement complexe", "Perfusion à domicile", "Toilette médicalisée"],
    commonConditionsAr: ["الرعاية بعد العملية", "الضمادات المعقدة", "التسريب في المنزل", "النظافة الطبية"],
    commonConditionsEn: ["Home dressing", "Home injection", "Home blood sampling", "Post-operative care"],
    averagePriceMad: { min: 100, max: 300 },
  },
  {
    slug: "audioprothesiste",
    nameFr: "Audioprothésiste",
    nameAr: "أخصائي السمعيات",
    nameEn: "Audiologist",
    icon: "🦻",
    featured: false,
    descriptionFr:
      "L'audioprothésiste évalue votre audition, propose et adapte vos appareils auditifs. Suivi et réglages réguliers pour optimiser la qualité d'écoute.",
    descriptionAr:
      "يقيّم أخصائي السمعيات قدرتك على السمع، يقترح ويضبط أجهزة السمع المناسبة. متابعة وضبط منتظم لتحسين جودة الاستماع.",
    descriptionEn:
      "The audiologist assesses your hearing, recommends and adjusts hearing aids. Regular follow-up and tuning to optimize listening quality.",
    commonConditions: ["Perte auditive", "Acouphènes", "Appareil auditif", "Bilan audiométrique"],
    commonConditionsAr: ["فقدان السمع", "طنين الأذن", "السماعة الطبية", "فحص السمع"],
    commonConditionsEn: ["Hearing aids", "Hearing loss", "Tinnitus", "Hearing test"],
    averagePriceMad: { min: 200, max: 800 },
  },
  {
    slug: "orthoptiste",
    nameFr: "Orthoptiste",
    nameAr: "أخصائي تقويم البصر",
    nameEn: "Orthoptist",
    icon: "👀",
    featured: false,
    descriptionFr:
      "L'orthoptiste rééduque les troubles de la vision binoculaire et des mouvements oculaires : strabisme, fatigue visuelle, troubles de la convergence chez l'enfant et l'adulte.",
    descriptionAr:
      "يعيد أخصائي تقويم البصر تأهيل اضطرابات الرؤية الثنائية وحركات العين: الحول، إجهاد العين، اضطرابات التقارب لدى الأطفال والبالغين.",
    descriptionEn:
      "The orthoptist treats disorders of binocular vision and eye movements: strabismus, visual fatigue, convergence disorders in children and adults.",
    commonConditions: ["Strabisme", "Fatigue visuelle", "Rééducation orthoptique", "Bilan vision binoculaire"],
    commonConditionsAr: ["الحول", "إجهاد العين", "إعادة تأهيل البصر", "فحص الرؤية الثنائية"],
    commonConditionsEn: ["Strabismus", "Amblyopia", "Visual rehabilitation", "Convergence disorders"],
    averagePriceMad: { min: 200, max: 350 },
  },
  {
    slug: "ergotherapeute",
    nameFr: "Ergothérapeute",
    nameAr: "أخصائي العلاج الوظيفي",
    nameEn: "Occupational therapist",
    icon: "✋",
    featured: false,
    descriptionFr:
      "L'ergothérapeute aide à retrouver l'autonomie dans les gestes du quotidien après accident, AVC ou maladie neurologique. Adaptations domiciliaires et apprentissage de nouvelles techniques.",
    descriptionAr:
      "يساعد أخصائي العلاج الوظيفي على استعادة الاستقلالية في الحركات اليومية بعد الحوادث، الجلطة الدماغية أو الأمراض العصبية. التكيفات المنزلية وتعلم تقنيات جديدة.",
    descriptionEn:
      "The occupational therapist helps regain autonomy in daily activities after accident, stroke or neurological disease. Home adaptations and new technique learning.",
    commonConditions: ["Rééducation post-AVC", "Trouble du développement enfant", "Adaptation domicile", "Rééducation main"],
    commonConditionsAr: ["إعادة التأهيل بعد السكتة الدماغية", "اضطرابات نمو الطفل", "تكييف المنزل", "إعادة تأهيل اليد"],
    commonConditionsEn: ["Functional rehabilitation", "Disability adaptation", "Fine motor skills", "Daily living autonomy"],
    averagePriceMad: { min: 250, max: 450 },
  },
  {
    slug: "psychomotricien",
    nameFr: "Psychomotricien(ne)",
    nameAr: "أخصائي العلاج النفسي الحركي",
    nameEn: "Psychomotor therapist",
    icon: "🧒",
    featured: false,
    descriptionFr:
      "Le psychomotricien intervient sur les troubles du développement moteur et psychomoteur chez l'enfant (TDAH, autisme, dyspraxie) et l'adulte. Approche corporelle de la psychologie.",
    descriptionAr:
      "يتدخل أخصائي العلاج النفسي الحركي على اضطرابات النمو الحركي والنفسي الحركي عند الأطفال (فرط الحركة، التوحد، عسر الحركة) والبالغين. نهج جسدي لعلم النفس.",
    descriptionEn:
      "The psychomotor therapist treats motor and psychomotor development disorders in children (ADHD, autism, dyspraxia) and adults. Bodily approach to psychology.",
    commonConditions: ["TDAH enfant", "Autisme", "Dyspraxie", "Retard psychomoteur", "Anxiété adulte"],
    commonConditionsAr: ["فرط الحركة عند الأطفال", "التوحد", "عسر الحركة", "تأخر النمو الحركي النفسي", "القلق عند البالغين"],
    commonConditionsEn: ["Psychomotor disorders", "Dyspraxia", "Childhood hyperactivity", "Body awareness", "Coordination"],
    averagePriceMad: { min: 250, max: 450 },
  },
  {
    slug: "dieteticien",
    nameFr: "Diététicien(ne)",
    nameAr: "أخصائي التغذية",
    nameEn: "Dietitian",
    icon: "🥗",
    featured: false,
    descriptionFr:
      "Le diététicien établit un plan alimentaire personnalisé pour la perte de poids, les pathologies digestives, le diabète, le sport ou la grossesse. Approche éducation nutritionnelle.",
    descriptionAr:
      "يضع أخصائي التغذية خطة غذائية مخصصة لإنقاص الوزن، أمراض الجهاز الهضمي، السكري، الرياضة أو الحمل. نهج التثقيف الغذائي.",
    descriptionEn:
      "The dietitian creates a personalized meal plan for weight loss, digestive conditions, diabetes, sports or pregnancy. Nutritional education approach.",
    commonConditions: ["Perte de poids", "Diabète", "Cholestérol", "Nutrition sportive", "Alimentation grossesse"],
    commonConditionsAr: ["فقدان الوزن", "السكري", "الكوليسترول", "التغذية الرياضية", "تغذية الحامل"],
    commonConditionsEn: ["Weight loss", "Therapeutic diet", "Eating disorders", "Diabetes", "High cholesterol"],
    averagePriceMad: { min: 200, max: 500 },
  },
  {
    slug: "manipulateur-radio",
    nameFr: "Manipulateur en radiologie",
    nameAr: "تقني الأشعة",
    nameEn: "Radiology technician",
    icon: "📷",
    featured: false,
    descriptionFr:
      "Le manipulateur en radiologie réalise les examens d'imagerie médicale : radiographies, scanner, IRM, mammographie, sous la responsabilité du radiologue.",
    descriptionAr:
      "يقوم تقني الأشعة بإجراء فحوصات التصوير الطبي: الأشعة السينية، التصوير المقطعي، الرنين المغناطيسي، تصوير الثدي، تحت مسؤولية طبيب الأشعة.",
    descriptionEn:
      "The radiology technician performs medical imaging exams: X-rays, CT scan, MRI, mammography, under the radiologist's supervision.",
    commonConditions: ["Radiographie", "Scanner", "IRM", "Mammographie", "Échographie"],
    commonConditionsAr: ["التصوير الشعاعي", "الأشعة المقطعية", "الرنين المغناطيسي", "تصوير الثدي", "تصوير بالموجات فوق الصوتية"],
    commonConditionsEn: ["X-ray", "CT scan", "MRI", "Mammography", "Ultrasound"],
    averagePriceMad: { min: 150, max: 400 },
  },

  // ═══════════════════ SOUS-SPÉCIALITÉS MÉDICALES ═══════════════════
  {
    slug: "cardiopediatre",
    nameFr: "Cardiologue pédiatrique",
    nameAr: "طبيب قلب الأطفال",
    nameEn: "Pediatric cardiologist",
    icon: "💗",
    featured: false,
    descriptionFr:
      "Le cardiologue pédiatrique diagnostique et traite les malformations cardiaques congénitales et les pathologies cardiovasculaires chez le nourrisson, l'enfant et l'adolescent.",
    descriptionAr:
      "يشخّص ويعالج طبيب قلب الأطفال التشوهات القلبية الخلقية وأمراض القلب والأوعية الدموية لدى الرضع والأطفال والمراهقين.",
    descriptionEn:
      "The pediatric cardiologist diagnoses and treats congenital heart defects and cardiovascular pathologies in infants, children and adolescents.",
    commonConditions: ["Malformation cardiaque", "Souffle au cœur enfant", "Suivi cardiopathie congénitale", "Échocardiographie enfant"],
    commonConditionsAr: ["التشوهات القلبية", "نفخة قلب الطفل", "متابعة اعتلال القلب الخلقي", "تخطيط صدى القلب للأطفال"],
    commonConditionsEn: ["Congenital heart disease", "Heart murmur", "Paediatric arrhythmia", "Cardiac malformations"],
    averagePriceMad: { min: 500, max: 1000 },
  },
  {
    slug: "gyneco-obstetricien",
    nameFr: "Gynécologue-obstétricien",
    nameAr: "طبيب النساء والتوليد",
    nameEn: "Obstetrician-gynecologist",
    icon: "🤰",
    featured: false,
    descriptionFr:
      "Le gynécologue-obstétricien assure le suivi de grossesse, l'accouchement et le post-partum, en plus de la gynécologie médicale et chirurgicale. Praticien hospitalier ou en cabinet.",
    descriptionAr:
      "يضمن طبيب النساء والتوليد متابعة الحمل، الولادة وما بعدها، إضافة إلى أمراض النساء الطبية والجراحية. ممارس في المستشفى أو في العيادة.",
    descriptionEn:
      "The obstetrician-gynecologist handles pregnancy follow-up, delivery and postpartum, plus medical and surgical gynecology. Hospital or office practitioner.",
    commonConditions: ["Suivi de grossesse", "Échographie obstétricale", "Accouchement", "Césarienne", "Post-partum"],
    commonConditionsAr: ["متابعة الحمل", "تصوير الحمل بالأمواج فوق الصوتية", "الولادة", "الولادة القيصرية", "ما بعد الولادة"],
    commonConditionsEn: ["Pregnancy", "Childbirth", "High-risk pregnancy", "Caesarean section", "Pregnancy follow-up"],
    averagePriceMad: { min: 400, max: 1000 },
  },
  {
    slug: "medecin-reproduction",
    nameFr: "Médecin de la reproduction",
    nameAr: "طبيب الإنجاب",
    nameEn: "Reproductive medicine specialist",
    icon: "👶",
    featured: false,
    descriptionFr:
      "Le médecin de la reproduction prend en charge les couples infertiles : bilan d'infertilité, stimulation ovarienne, insémination, FIV, ICSI. Centres PMA agréés.",
    descriptionAr:
      "يتولى طبيب الإنجاب رعاية الأزواج الذين يعانون من العقم: تقييم العقم، تحفيز المبيض، التلقيح الاصطناعي، أطفال الأنابيب، الحقن المجهري. مراكز معتمدة.",
    descriptionEn:
      "The reproductive medicine specialist treats infertile couples: infertility assessment, ovarian stimulation, insemination, IVF, ICSI. Approved fertility centers.",
    commonConditions: ["Infertilité", "FIV", "Insémination", "Stimulation ovarienne", "PMA"],
    commonConditionsAr: ["العقم", "أطفال الأنابيب", "التلقيح الاصطناعي", "تحفيز المبيض", "الإنجاب المساعد"],
    commonConditionsEn: ["Infertility", "IVF", "Artificial insemination", "PCOS", "Endometriosis"],
    averagePriceMad: { min: 600, max: 1500 },
  },
  {
    slug: "medecin-douleur",
    nameFr: "Médecin de la douleur",
    nameAr: "طبيب الألم",
    nameEn: "Pain specialist",
    icon: "⚡",
    featured: false,
    descriptionFr:
      "Le médecin algologue prend en charge les douleurs chroniques rebelles : douleurs neuropathiques, fibromyalgie, migraines, lombalgies. Techniques médicamenteuses et interventionnelles.",
    descriptionAr:
      "يتولى طبيب الألم رعاية الآلام المزمنة المستعصية: الألم العصبي، الفيبروميالغيا، الصداع النصفي، آلام الظهر. تقنيات دوائية وتدخلية.",
    descriptionEn:
      "The pain specialist treats chronic intractable pain: neuropathic pain, fibromyalgia, migraines, low back pain. Medication and interventional techniques.",
    commonConditions: ["Douleur chronique", "Migraine", "Lombalgie chronique", "Fibromyalgie", "Douleur neuropathique"],
    commonConditionsAr: ["الألم المزمن", "الصداع النصفي", "آلام أسفل الظهر المزمنة", "الفيبروميالغيا", "الألم العصبي"],
    commonConditionsEn: ["Chronic pain", "Fibromyalgia", "Neuropathic pain", "Migraine", "Cancer pain"],
    averagePriceMad: { min: 400, max: 800 },
  },
  {
    slug: "soins-palliatifs",
    nameFr: "Médecin en soins palliatifs",
    nameAr: "طبيب الرعاية الملطفة",
    nameEn: "Palliative care physician",
    icon: "🕊️",
    featured: false,
    descriptionFr:
      "Le médecin en soins palliatifs accompagne les patients en fin de vie ou atteints de pathologies graves : gestion de la douleur, confort, soutien psychologique du patient et de la famille.",
    descriptionAr:
      "يرافق طبيب الرعاية الملطفة المرضى في نهاية حياتهم أو المصابين بأمراض خطيرة: إدارة الألم، الراحة، الدعم النفسي للمريض والعائلة.",
    descriptionEn:
      "The palliative care physician accompanies end-of-life patients or those with serious illness: pain management, comfort, psychological support for patient and family.",
    commonConditions: ["Soins de confort", "Gestion douleur cancer", "Accompagnement fin de vie", "Soutien familial"],
    commonConditionsAr: ["الرعاية الملطفة", "إدارة ألم السرطان", "مرافقة نهاية الحياة", "الدعم الأسري"],
    commonConditionsEn: ["End of life", "Pain management", "Comfort care", "Family support"],
    averagePriceMad: { min: 300, max: 700 },
  },
  {
    slug: "echographiste",
    nameFr: "Échographiste",
    nameAr: "أخصائي الموجات فوق الصوتية",
    nameEn: "Sonographer",
    icon: "🩻",
    featured: false,
    descriptionFr:
      "L'échographiste réalise les échographies diagnostiques : obstétricale, abdominale, thyroïdienne, mammaire, cardiaque, doppler vasculaire. Médecin radiologue ou spécialiste dédié.",
    descriptionAr:
      "يقوم أخصائي الموجات فوق الصوتية بإجراء الفحوصات التشخيصية: الحمل، البطن، الغدة الدرقية، الثدي، القلب، دوبلر الأوعية الدموية. طبيب أشعة أو متخصص.",
    descriptionEn:
      "The sonographer performs diagnostic ultrasounds: obstetric, abdominal, thyroid, breast, cardiac, vascular doppler. Radiologist or dedicated specialist.",
    commonConditions: ["Échographie grossesse", "Échographie abdominale", "Échographie thyroïde", "Doppler vasculaire"],
    commonConditionsAr: ["تصوير الحمل بالأمواج فوق الصوتية", "تصوير البطن بالأمواج", "تصوير الغدة الدرقية", "دوبلر الأوعية الدموية"],
    commonConditionsEn: ["Obstetric ultrasound", "Abdominal ultrasound", "Cardiac ultrasound", "Doppler"],
    averagePriceMad: { min: 250, max: 600 },
  },
  {
    slug: "medecin-nucleaire",
    nameFr: "Médecin nucléaire",
    nameAr: "طبيب الطب النووي",
    nameEn: "Nuclear medicine physician",
    icon: "☢️",
    featured: false,
    descriptionFr:
      "Le médecin nucléaire réalise et interprète scintigraphies et examens PET-scan : cancérologie, cardiologie, neurologie, endocrinologie. Diagnostic et thérapeutique radio-isotopique.",
    descriptionAr:
      "يجري ويفسّر طبيب الطب النووي التصوير الومضاني وفحوصات PET-scan: الأورام، القلب، الأعصاب، الغدد. التشخيص والعلاج بالنظائر المشعة.",
    descriptionEn:
      "The nuclear medicine physician performs and interprets scintigraphy and PET-scan: oncology, cardiology, neurology, endocrinology. Radioisotope diagnostics and therapy.",
    commonConditions: ["Scintigraphie osseuse", "PET-scan oncologie", "Scintigraphie thyroïde", "Iode radioactif"],
    commonConditionsAr: ["التصوير الومضاني للعظام", "PET-scan للأورام", "التصوير الومضاني للغدة الدرقية", "اليود المشع"],
    commonConditionsEn: ["Scintigraphy", "PET scan", "Radiotherapy", "Thyroid"],
    averagePriceMad: { min: 1500, max: 4000 },
  },

  // ═══════════════════ MÉDECINES ALTERNATIVES ═══════════════════
  {
    slug: "acupuncteur",
    nameFr: "Acupuncteur",
    nameAr: "أخصائي الوخز بالإبر",
    nameEn: "Acupuncturist",
    icon: "📍",
    featured: false,
    descriptionFr:
      "L'acupuncteur traite par stimulation de points précis du corps via des aiguilles : douleurs chroniques, stress, troubles digestifs, infertilité, accompagnement de la grossesse.",
    descriptionAr:
      "يعالج أخصائي الوخز بالإبر عن طريق تحفيز نقاط محددة في الجسم بواسطة الإبر: الآلام المزمنة، التوتر، اضطرابات الجهاز الهضمي، العقم، مرافقة الحمل.",
    descriptionEn:
      "The acupuncturist treats by stimulating specific body points with needles: chronic pain, stress, digestive disorders, infertility, pregnancy support.",
    commonConditions: ["Douleur chronique", "Stress et anxiété", "Migraine", "Infertilité", "Sevrage tabagique"],
    commonConditionsAr: ["الألم المزمن", "التوتر والقلق", "الصداع النصفي", "العقم", "الإقلاع عن التدخين"],
    commonConditionsEn: ["Chronic pain", "Stress", "Migraine", "Insomnia", "Infertility"],
    averagePriceMad: { min: 250, max: 500 },
  },
  {
    slug: "homeopathe",
    nameFr: "Homéopathe",
    nameAr: "أخصائي المعالجة المثلية",
    nameEn: "Homeopath",
    icon: "🌿",
    featured: false,
    descriptionFr:
      "L'homéopathe prescrit des dilutions issues de substances naturelles pour stimuler les défenses de l'organisme : troubles fonctionnels, allergies, pathologies chroniques.",
    descriptionAr:
      "يصف أخصائي المعالجة المثلية تخفيفات من مواد طبيعية لتحفيز دفاعات الجسم: الاضطرابات الوظيفية، الحساسية، الأمراض المزمنة.",
    descriptionEn:
      "The homeopath prescribes diluted natural substances to stimulate the body's defenses: functional disorders, allergies, chronic conditions.",
    commonConditions: ["Allergies", "Troubles du sommeil", "Stress", "Eczéma", "Rhinite chronique"],
    commonConditionsAr: ["الحساسية", "اضطرابات النوم", "التوتر", "الإكزيما", "التهاب الأنف المزمن"],
    commonConditionsEn: ["Allergies", "Stress", "Insomnia", "Digestive disorders", "Chronic diseases"],
    averagePriceMad: { min: 200, max: 500 },
  },
  {
    slug: "chiropracteur",
    nameFr: "Chiropracteur",
    nameAr: "أخصائي تقويم العمود الفقري",
    nameEn: "Chiropractor",
    icon: "🦴",
    featured: false,
    descriptionFr:
      "Le chiropracteur traite les troubles musculo-squelettiques par manipulations vertébrales et articulaires : lombalgies, cervicalgies, sciatique, hernies discales.",
    descriptionAr:
      "يعالج أخصائي تقويم العمود الفقري الاضطرابات العضلية الهيكلية عن طريق المعالجة اليدوية للفقرات والمفاصل: آلام الظهر، آلام الرقبة، عرق النسا، الانزلاق الغضروفي.",
    descriptionEn:
      "The chiropractor treats musculoskeletal disorders through vertebral and joint manipulations: low back pain, neck pain, sciatica, disc hernia.",
    commonConditions: ["Lombalgie", "Cervicalgie", "Sciatique", "Hernie discale", "Torticolis"],
    commonConditionsAr: ["آلام أسفل الظهر", "آلام الرقبة", "عرق النسا", "الانزلاق الغضروفي", "تشنج الرقبة"],
    commonConditionsEn: ["Lower back pain", "Neck pain", "Herniated disc", "Headaches", "Sciatica"],
    averagePriceMad: { min: 350, max: 700 },
  },
  {
    slug: "naturopathe",
    nameFr: "Naturopathe",
    nameAr: "أخصائي العلاج الطبيعي",
    nameEn: "Naturopath",
    icon: "🌱",
    featured: false,
    descriptionFr:
      "Le naturopathe propose une approche globale de la santé par l'alimentation, les plantes, l'hygiène de vie : fatigue chronique, troubles digestifs, immunité, équilibre hormonal.",
    descriptionAr:
      "يقترح أخصائي العلاج الطبيعي نهجاً شاملاً للصحة عبر التغذية، النباتات، نمط الحياة: الإرهاق المزمن، اضطرابات الهضم، المناعة، التوازن الهرموني.",
    descriptionEn:
      "The naturopath offers a holistic health approach through nutrition, plants, lifestyle: chronic fatigue, digestive disorders, immunity, hormonal balance.",
    commonConditions: ["Fatigue chronique", "Troubles digestifs", "Immunité", "Stress oxydatif", "Équilibre hormonal"],
    commonConditionsAr: ["الإرهاق المزمن", "اضطرابات الهضم", "تعزيز المناعة", "الإجهاد التأكسدي", "التوازن الهرموني"],
    commonConditionsEn: ["Detox", "Personalised nutrition", "Stress", "Fatigue", "Digestive disorders"],
    averagePriceMad: { min: 300, max: 600 },
  },
  {
    slug: "tabacologue",
    nameFr: "Tabacologue",
    nameAr: "أخصائي إقلاع التدخين",
    nameEn: "Tobacco specialist",
    icon: "🚭",
    featured: false,
    descriptionFr:
      "Le tabacologue accompagne le sevrage tabagique : évaluation de la dépendance, prescription de substituts nicotiniques, thérapie cognitive et comportementale.",
    descriptionAr:
      "يرافق أخصائي إقلاع التدخين في الإقلاع: تقييم الإدمان، وصف بدائل النيكوتين، العلاج المعرفي السلوكي.",
    descriptionEn:
      "The tobacco specialist supports smoking cessation: dependency assessment, nicotine replacement prescription, cognitive behavioral therapy.",
    commonConditions: ["Sevrage tabagique", "Dépendance nicotinique", "Vapotage", "Rechute tabac"],
    commonConditionsAr: ["الإقلاع عن التدخين", "الإدمان النيكوتيني", "السجائر الإلكترونية", "الانتكاسة"],
    commonConditionsEn: ["Smoking cessation", "Nicotine addiction", "Vaping", "Relapse prevention"],
    averagePriceMad: { min: 250, max: 500 },
  },

  // ═══════════════════ CHIRURGIES FINES ═══════════════════
  {
    slug: "chirurgien-main",
    nameFr: "Chirurgien de la main",
    nameAr: "جراح اليد",
    nameEn: "Hand surgeon",
    icon: "✋",
    featured: false,
    descriptionFr:
      "Le chirurgien de la main traite les pathologies traumatiques et dégénératives du membre supérieur : canal carpien, Dupuytren, fractures, traumatismes tendineux et nerveux.",
    descriptionAr:
      "يعالج جراح اليد الأمراض الرضحية والتنكسية للطرف العلوي: النفق الرسغي، انكماش دوبويتران، الكسور، إصابات الأوتار والأعصاب.",
    descriptionEn:
      "The hand surgeon treats traumatic and degenerative upper limb conditions: carpal tunnel, Dupuytren's, fractures, tendon and nerve injuries.",
    commonConditions: ["Canal carpien", "Maladie de Dupuytren", "Fracture main", "Doigt à ressaut", "Rhizarthrose"],
    commonConditionsAr: ["متلازمة النفق الرسغي", "انكماش دوبويتران", "كسر اليد", "الإصبع الزنادي", "فصال مفصل الإبهام"],
    commonConditionsEn: ["Carpal tunnel syndrome", "Dupuytren's contracture", "Hand fracture", "Tendon injury", "Hand trauma"],
    averagePriceMad: { min: 600, max: 1500 },
  },
  {
    slug: "chirurgien-oral",
    nameFr: "Chirurgien oral",
    nameAr: "جراح الفم",
    nameEn: "Oral surgeon",
    icon: "👄",
    featured: false,
    descriptionFr:
      "Le chirurgien oral réalise les interventions complexes de la cavité buccale : extractions difficiles, implants, kystes, biopsies, chirurgie pré-prothétique. Distinct du chirurgien maxillo-facial.",
    descriptionAr:
      "يقوم جراح الفم بالتدخلات المعقدة في تجويف الفم: الخلع الصعب، الزراعة، الأكياس، الخزعات، الجراحة قبل التركيب. يختلف عن جراح الفكين والوجه.",
    descriptionEn:
      "The oral surgeon performs complex oral cavity procedures: difficult extractions, implants, cysts, biopsies, pre-prosthetic surgery. Distinct from maxillofacial surgeon.",
    commonConditions: ["Extraction dent de sagesse", "Implant dentaire", "Kyste mâchoire", "Biopsie buccale"],
    commonConditionsAr: ["خلع ضرس العقل", "زراعة الأسنان", "كيس الفك", "خزعة الفم"],
    commonConditionsEn: ["Dental implant", "Wisdom teeth", "Bone graft", "Oral cyst"],
    averagePriceMad: { min: 800, max: 3000 },
  },
  {
    slug: "chirurgien-endocrinien",
    nameFr: "Chirurgien endocrinien",
    nameAr: "جراح الغدد",
    nameEn: "Endocrine surgeon",
    icon: "⚗️",
    featured: false,
    descriptionFr:
      "Le chirurgien endocrinien opère les glandes endocrines : thyroïde, parathyroïdes, surrénales, pancréas. Goitres, nodules thyroïdiens, hyperparathyroïdie, tumeurs surrénaliennes.",
    descriptionAr:
      "يجري جراح الغدد عمليات الغدد الصماء: الغدة الدرقية، جارات الدرقية، الكظرية، البنكرياس. تضخم الدرقية، عقد الدرقية، فرط نشاط جارات الدرقية، أورام الكظرية.",
    descriptionEn:
      "The endocrine surgeon operates on endocrine glands: thyroid, parathyroid, adrenals, pancreas. Goiters, thyroid nodules, hyperparathyroidism, adrenal tumors.",
    commonConditions: ["Nodule thyroïdien", "Goitre", "Hyperparathyroïdie", "Tumeur surrénale", "Cancer thyroïde"],
    commonConditionsAr: ["عقدة الغدة الدرقية", "تضخم الغدة الدرقية", "فرط نشاط جارات الدرقية", "ورم الغدة الكظرية", "سرطان الغدة الدرقية"],
    commonConditionsEn: ["Thyroid", "Parathyroid", "Adrenal glands", "Endocrine tumour", "Goitre"],
    averagePriceMad: { min: 1000, max: 2500 },
  },
  {
    slug: "oncochirurgien",
    nameFr: "Chirurgien cancérologue",
    nameAr: "جراح الأورام",
    nameEn: "Surgical oncologist",
    icon: "🎗️",
    featured: false,
    descriptionFr:
      "Le chirurgien cancérologue opère les tumeurs : sein, digestif, gynécologique, ORL, sarcomes. Approche multidisciplinaire en lien avec oncologue médical et radiothérapeute.",
    descriptionAr:
      "يجري جراح الأورام عمليات الأورام: الثدي، الجهاز الهضمي، أمراض النساء، الأنف والأذن والحنجرة، الساركوما. نهج متعدد التخصصات مع طبيب الأورام والمعالج بالأشعة.",
    descriptionEn:
      "The surgical oncologist operates on tumors: breast, digestive, gynecologic, ENT, sarcomas. Multidisciplinary approach with medical oncologist and radiotherapist.",
    commonConditions: ["Cancer du sein", "Cancer colorectal", "Cancer estomac", "Cancer gynécologique", "Sarcome"],
    commonConditionsAr: ["استئصال الأورام", "سرطان الثدي", "سرطان القولون والمستقيم", "تنظيف العقد اللمفية", "إعادة البناء"],
    commonConditionsEn: ["Tumour removal", "Breast cancer", "Colorectal cancer", "Lymph node dissection", "Reconstruction"],
    averagePriceMad: { min: 1500, max: 4000 },
  },
  {
    slug: "chirurgien-face-cou",
    nameFr: "Chirurgien de la face et du cou",
    nameAr: "جراح الوجه والعنق",
    nameEn: "Head and neck surgeon",
    icon: "💆",
    featured: false,
    descriptionFr:
      "Le chirurgien de la face et du cou opère les tumeurs et malformations de la sphère ORL et cervicale : larynx, pharynx, glandes salivaires, thyroïde, ganglions cervicaux.",
    descriptionAr:
      "يجري جراح الوجه والعنق عمليات الأورام والتشوهات في منطقة الأنف والأذن والحنجرة والرقبة: الحنجرة، البلعوم، الغدد اللعابية، الغدة الدرقية، العقد اللمفية الرقبية.",
    descriptionEn:
      "The head and neck surgeon operates on tumors and malformations of the ENT and cervical area: larynx, pharynx, salivary glands, thyroid, cervical nodes.",
    commonConditions: ["Cancer larynx", "Tumeur glandes salivaires", "Curage ganglionnaire", "Cancer thyroïde", "Tumeur cou"],
    commonConditionsAr: ["الغدة الدرقية", "الغدة النكفية", "ورم الرقبة", "إعادة بناء الوجه", "تنظيف العقد اللمفية"],
    commonConditionsEn: ["Thyroid", "Parotid gland", "Cervical tumour", "Facial reconstruction", "Lymph node dissection"],
    averagePriceMad: { min: 1200, max: 3500 },
  },

  // ═══════════════════ MÉDECINE ACADÉMIQUE / HOSPITALIÈRE ═══════════════════
  {
    slug: "addictologue",
    nameFr: "Addictologue",
    nameAr: "أخصائي الإدمان",
    nameEn: "Addictologist",
    icon: "🆘",
    featured: false,
    descriptionFr:
      "L'addictologue prend en charge les addictions : alcool, drogues, médicaments, jeux, écrans. Approche médico-psychologique, sevrage encadré, suivi rechute.",
    descriptionAr:
      "يتولى أخصائي الإدمان رعاية حالات الإدمان: الكحول، المخدرات، الأدوية، القمار، الشاشات. نهج طبي نفسي، إقلاع تحت الإشراف، متابعة الانتكاسة.",
    descriptionEn:
      "The addictologist treats addictions: alcohol, drugs, medications, gambling, screens. Medical-psychological approach, supervised detox, relapse follow-up.",
    commonConditions: ["Alcoolisme", "Toxicomanie", "Addiction médicaments", "Addiction jeux", "Sevrage alcool"],
    commonConditionsAr: ["الإدمان على الكحول", "إدمان المخدرات", "إدمان الأدوية", "إدمان القمار", "علاج الإقلاع عن الكحول"],
    commonConditionsEn: ["Alcoholism", "Drug addiction", "Smoking", "Behavioural addictions", "Withdrawal"],
    averagePriceMad: { min: 350, max: 700 },
  },
  {
    slug: "infectiologue",
    nameFr: "Infectiologue",
    nameAr: "طبيب الأمراض المعدية",
    nameEn: "Infectious disease specialist",
    icon: "🦠",
    featured: false,
    descriptionFr:
      "L'infectiologue prend en charge les maladies infectieuses complexes : VIH, hépatites virales, tuberculose, infections post-voyage, infections nosocomiales, résistance aux antibiotiques.",
    descriptionAr:
      "يتولى طبيب الأمراض المعدية الأمراض المعدية المعقدة: فيروس نقص المناعة البشرية، التهابات الكبد الفيروسية، السل، عدوى ما بعد السفر، عدوى المستشفيات.",
    descriptionEn:
      "The infectious disease specialist treats complex infections: HIV, viral hepatitis, tuberculosis, post-travel infections, hospital-acquired infections, antibiotic resistance.",
    commonConditions: ["VIH/SIDA", "Hépatite B/C", "Tuberculose", "Infection post-voyage", "Infection résistante"],
    commonConditionsAr: ["فيروس نقص المناعة البشرية", "التهاب الكبد", "السل", "الأمراض الاستوائية", "العدوى الحادة"],
    commonConditionsEn: ["HIV", "Hepatitis", "Tuberculosis", "Tropical diseases", "Severe infections"],
    averagePriceMad: { min: 400, max: 900 },
  },
  {
    slug: "medecin-genetique",
    nameFr: "Médecin généticien",
    nameAr: "طبيب الوراثة",
    nameEn: "Medical geneticist",
    icon: "🧬",
    featured: false,
    descriptionFr:
      "Le généticien identifie les maladies héréditaires et conseille les familles : maladies rares, conseil pré-conceptionnel, dépistage prénatal, tests prédictifs.",
    descriptionAr:
      "يحدد طبيب الوراثة الأمراض الوراثية ويقدم النصح للعائلات: الأمراض النادرة، الاستشارة قبل الحمل، الفحص قبل الولادة، الاختبارات التنبؤية.",
    descriptionEn:
      "The medical geneticist identifies hereditary diseases and counsels families: rare diseases, preconception counseling, prenatal screening, predictive tests.",
    commonConditions: ["Maladie rare", "Conseil génétique", "Dépistage prénatal", "Trisomie 21", "Mutation BRCA"],
    commonConditionsAr: ["الأمراض النادرة", "الاستشارة الوراثية", "الفحص قبل الولادة", "متلازمة داون", "طفرة BRCA"],
    commonConditionsEn: ["Rare disease", "Genetic counseling", "Prenatal screening", "Down syndrome", "BRCA mutation"],
    averagePriceMad: { min: 600, max: 1500 },
  },
  {
    slug: "cancerologue",
    nameFr: "Cancérologue",
    nameAr: "أخصائي السرطان",
    nameEn: "Cancer specialist",
    icon: "🎀",
    featured: false,
    descriptionFr:
      "Le cancérologue diagnostique et coordonne le traitement des cancers : chimiothérapie, hormonothérapie, immunothérapie, thérapies ciblées. Vue d'ensemble du parcours de soins.",
    descriptionAr:
      "يشخّص أخصائي السرطان وينسّق علاج السرطانات: العلاج الكيميائي، العلاج الهرموني، العلاج المناعي، العلاجات المستهدفة. نظرة شاملة على مسار العلاج.",
    descriptionEn:
      "The cancer specialist diagnoses and coordinates cancer treatment: chemotherapy, hormone therapy, immunotherapy, targeted therapies. Overall care pathway view.",
    commonConditions: ["Chimiothérapie", "Hormonothérapie", "Immunothérapie", "Suivi post-cancer", "Récidive"],
    commonConditionsAr: ["العلاج الكيميائي", "العلاج الهرموني", "العلاج المناعي", "متابعة ما بعد السرطان", "الانتكاسة"],
    commonConditionsEn: ["Chemotherapy", "Hormone therapy", "Immunotherapy", "Post-cancer follow-up", "Recurrence"],
    averagePriceMad: { min: 500, max: 1200 },
  },
  {
    slug: "medecin-legiste",
    nameFr: "Médecin légiste",
    nameAr: "الطبيب الشرعي",
    nameEn: "Forensic doctor",
    icon: "⚖️",
    featured: false,
    descriptionFr:
      "Le médecin légiste réalise les expertises médico-légales : examens de victimes, certificats de coups et blessures, autopsies. Activité majoritairement judiciaire.",
    descriptionAr:
      "يقوم الطبيب الشرعي بالخبرات الطبية القانونية: فحص الضحايا، شهادات الضرب والجروح، التشريح. النشاط في الغالب قضائي.",
    descriptionEn:
      "The forensic doctor performs medico-legal expertise: victim examinations, assault certificates, autopsies. Mostly judicial activity.",
    commonConditions: ["Certificat coups et blessures", "Expertise médico-légale", "Examen victime", "Autopsie"],
    commonConditionsAr: ["شهادة الضرب والجرح", "الخبرة الطبية الشرعية", "فحص الضحايا", "التشريح"],
    commonConditionsEn: ["Assault certificate", "Medico-legal expertise", "Victim examination", "Autopsy"],
    averagePriceMad: { min: 500, max: 1500 },
  },
  {
    slug: "pharmacien",
    nameFr: "Pharmacien",
    nameAr: "صيدلي",
    nameEn: "Pharmacist",
    icon: "💊",
    featured: false,
    descriptionFr:
      "Le pharmacien d'officine délivre les médicaments prescrits, conseille sur l'automédication, vérifie les interactions, vaccine. Pharmacie de garde 24h/24 dans chaque ville.",
    descriptionAr:
      "يصرف الصيدلي الأدوية الموصوفة، يقدم النصح حول العلاج الذاتي، يفحص التفاعلات، يطعّم. صيدلية الحراسة 24 ساعة في كل مدينة.",
    descriptionEn:
      "The pharmacist dispenses prescribed medications, advises on self-medication, checks interactions, vaccinates. 24/7 on-call pharmacy in each city.",
    commonConditions: ["Délivrance ordonnance", "Conseil automédication", "Vaccination", "Renouvellement chronique"],
    commonConditionsAr: ["صرف الوصفة الطبية", "نصائح التداوي الذاتي", "التلقيح", "تجديد الأدوية المزمنة"],
    commonConditionsEn: ["Prescription dispensing", "Self-medication advice", "Vaccination", "Chronic refill"],
    averagePriceMad: { min: 0, max: 50 },
  },
];
